import * as DialogPrimitive from "@radix-ui/react-dialog";
import React, { useEffect, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { ContactModel } from "@/apidtos";
import { useGeography } from "@core/hooks/geographyHooks";
import { Dialog, DialogTrigger } from "@core/components/ui/dialog";
import { isPOBox, PO_BOX_MESSAGE } from "@/lib/poBox";

/**
 * SPG-styled Address modal used by the site dashboard's Address Book route.
 * Renders the form via raw `.addr-form-*` classes (defined in spg-addresses-style.css)
 * so the dark theme + yellow Bebas Neue title match the dripbar mock-up.
 */
export type SiteAddressModalProps = React.PropsWithChildren & {
    contact?: ContactModel;
    onConfirm?: (contact: ContactModel) => void;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
};

type AddressFormValues = {
    DisplayName: string;
    Street1: string;
    Street2?: string;
    Street3?: string;
    City: string;
    RegionId?: number | null;
    PostalCode: string;
    CountryId?: number | null;
    Email?: string;
    PhoneNumber: string;
    Instructions?: string;
};

export const SiteAddressModal: React.FC<SiteAddressModalProps> = ({
    children,
    contact,
    onConfirm,
    open: openProp,
    onOpenChange,
}) => {
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
    const open = openProp ?? uncontrolledOpen;
    const setOpen = onOpenChange ?? setUncontrolledOpen;
    const { t } = useTranslation();
    const isEditing = Boolean(contact?.Id);
    const isControlled = openProp !== undefined;

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            {children != null
                ? children
                : !isControlled && (
                    <DialogTrigger asChild>
                        <button type="button" className="addr-add-btn">
                            {t("ui.addNewAddress", "Add New Address")}
                        </button>
                    </DialogTrigger>
                )}
            <DialogPrimitive.Portal>
                <DialogPrimitive.Overlay
                    className="addr-modal-overlay open"
                    style={{ opacity: 1, pointerEvents: "auto" }}
                />
                <DialogPrimitive.Content
                    className="addr-modal"
                    style={{
                        position: "fixed",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        zIndex: 1001,
                    }}
                    aria-describedby={undefined}
                    onPointerDownOutside={(event) => event.preventDefault()}
                    onInteractOutside={(event) => event.preventDefault()}
                    onEscapeKeyDown={(event) => event.preventDefault()}
                >
                    <DialogPrimitive.Title className="addr-modal-title">
                        {isEditing
                            ? t("ui.editAddress", "EDIT ADDRESS")
                            : t("ui.addNewAddress", "ADD NEW ADDRESS")}
                    </DialogPrimitive.Title>
                    <DialogPrimitive.Close className="addr-modal-close" aria-label={t("ui.close", "Close")}>
                        <X size={18} />
                    </DialogPrimitive.Close>
                    <SiteAddressForm
                        contact={contact}
                        onCancel={() => setOpen(false)}
                        onSubmit={(c) => {
                            onConfirm?.(c);
                            setOpen(false);
                        }}
                    />
                </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
        </Dialog>
    );
};

type SiteAddressFormProps = {
    contact?: ContactModel;
    onSubmit: (contact: ContactModel) => void;
    onCancel: () => void;
};

const SiteAddressForm: React.FC<SiteAddressFormProps> = ({ contact, onSubmit, onCancel }) => {
    const { t } = useTranslation();
    const { loading, data } = useGeography();
    const regions = data.regions?.Results ?? [];
    const countries = data.countries?.Results ?? [];

    const form = useForm<AddressFormValues>({
        defaultValues: {
            DisplayName: contact?.DisplayName ?? "",
            Street1: contact?.Street1 ?? "",
            Street2: contact?.Street2 ?? "",
            Street3: contact?.Street3 ?? "",
            City: contact?.City ?? "",
            RegionId: contact?.RegionId ?? undefined,
            PostalCode: contact?.PostalCode ?? "",
            CountryId: contact?.CountryId ?? undefined,
            Email: contact?.Email ?? "",
            PhoneNumber: contact?.PhoneNumber ?? "",
            Instructions: contact?.Instructions ?? "",
        },
    });

    const countryId = useWatch({ control: form.control, name: "CountryId" });
    const availableRegions = regions.filter((r) => r.CountryId === countryId);

    useEffect(() => {
        if (!loading && countries.length > 0 && !form.getValues("CountryId")) {
            form.setValue("CountryId", countries[0].Id);
        }
    }, [loading, countries, form]);

    const submit = form.handleSubmit((values) => {
        onSubmit({
            ...(contact ?? {}),
            ...values,
            IsActive: contact?.IsActive ?? true,
            IsDeleted: contact?.IsDeleted ?? false,
        } as ContactModel);
    });

    if (loading) {
        return (
            <p className="addr-form-label" style={{ textAlign: "center", padding: 24 }}>
                {t("ui.loading", "Loading...")}
            </p>
        );
    }

    const errors = form.formState.errors;

    return (
        <FormProvider {...form}>
            <form id="siteAddressForm" onSubmit={submit} noValidate>
                <div className="addr-form-grid">
                    <div className="addr-form-field addr-form-full">
                        <label className="addr-form-label" htmlFor="addr-displayname">
                            {t("ui.addressLabel", "Address Label")}
                        </label>
                        <input
                            id="addr-displayname"
                            className="addr-form-input"
                            placeholder={t("ui.addressLabelPlaceholder", "e.g., Main Office, Home, Warehouse")}
                            aria-invalid={errors.DisplayName ? "true" : "false"}
                            {...form.register("DisplayName", {
                                required: t("ui.required", "This field is required.") as string,
                            })}
                        />
                        {errors.DisplayName && (
                            <ErrText>{String(errors.DisplayName.message)}</ErrText>
                        )}
                    </div>

                    <div className="addr-form-field addr-form-full">
                        <label className="addr-form-label" htmlFor="addr-street1">
                            {t("ui.streetAddress", "Street Address")}
                        </label>
                        <input
                            id="addr-street1"
                            className="addr-form-input"
                            placeholder={t("ui.streetAddressPlaceholder", "Street address")}
                            aria-invalid={errors.Street1 ? "true" : "false"}
                            {...form.register("Street1", {
                                required: t("ui.required", "This field is required.") as string,
                                validate: (value) =>
                                    !isPOBox(value) || (t("ui.poBoxNotAllowed", PO_BOX_MESSAGE) as string),
                            })}
                        />
                        {errors.Street1 && <ErrText>{String(errors.Street1.message)}</ErrText>}
                    </div>

                    <div className="addr-form-field addr-form-full">
                        <label className="addr-form-label" htmlFor="addr-street2">
                            {t("ui.streetAddressLine2", "Street Address Line 2")}
                        </label>
                        <input
                            id="addr-street2"
                            className="addr-form-input"
                            placeholder={t("ui.streetAddressLine2Placeholder", "Apt, Suite, Unit (optional)")}
                            aria-invalid={errors.Street2 ? "true" : "false"}
                            {...form.register("Street2", {
                                validate: (value) =>
                                    !isPOBox(value) || (t("ui.poBoxNotAllowed", PO_BOX_MESSAGE) as string),
                            })}
                        />
                        {errors.Street2 && <ErrText>{String(errors.Street2.message)}</ErrText>}
                    </div>

                    <div className="addr-form-field">
                        <label className="addr-form-label" htmlFor="addr-city">
                            {t("ui.city", "City")}
                        </label>
                        <input
                            id="addr-city"
                            className="addr-form-input"
                            placeholder={t("ui.city", "City")}
                            aria-invalid={errors.City ? "true" : "false"}
                            {...form.register("City", {
                                required: t("ui.required", "This field is required.") as string,
                            })}
                        />
                        {errors.City && <ErrText>{String(errors.City.message)}</ErrText>}
                    </div>

                    <div className="addr-form-field">
                        <label className="addr-form-label" htmlFor="addr-state">
                            {t("ui.state", "State")}
                        </label>
                        <select
                            id="addr-state"
                            className="addr-form-input"
                            aria-invalid={errors.RegionId ? "true" : "false"}
                            disabled={availableRegions.length === 0}
                            {...form.register("RegionId", {
                                valueAsNumber: true,
                                validate: (value) =>
                                    availableRegions.length === 0 || (value != null && !Number.isNaN(value))
                                        ? true
                                        : (t("ui.pleaseSelectAState", "Please select a state.") as string),
                            })}
                        >
                            <option value="">{t("ui.state", "State")}</option>
                            {availableRegions.map((r) => (
                                <option key={r.Id} value={r.Id}>
                                    {r.Name}
                                </option>
                            ))}
                        </select>
                        {errors.RegionId && <ErrText>{String(errors.RegionId.message)}</ErrText>}
                    </div>

                    <div className="addr-form-field">
                        <label className="addr-form-label" htmlFor="addr-zip">
                            {t("ui.zipCode", "ZIP Code")}
                        </label>
                        <input
                            id="addr-zip"
                            className="addr-form-input"
                            placeholder={t("ui.zipCode", "ZIP Code")}
                            aria-invalid={errors.PostalCode ? "true" : "false"}
                            {...form.register("PostalCode", {
                                required: t("ui.required", "This field is required.") as string,
                            })}
                        />
                        {errors.PostalCode && <ErrText>{String(errors.PostalCode.message)}</ErrText>}
                    </div>

                    <div className="addr-form-field">
                        <label className="addr-form-label" htmlFor="addr-phone">
                            {t("ui.phone", "Phone")}
                        </label>
                        <input
                            id="addr-phone"
                            className="addr-form-input"
                            placeholder={t("ui.phoneNumberPlaceholder", "Phone number")}
                            aria-invalid={errors.PhoneNumber ? "true" : "false"}
                            {...form.register("PhoneNumber", {
                                required: t("ui.required", "This field is required.") as string,
                            })}
                        />
                        {errors.PhoneNumber && (
                            <ErrText>{String(errors.PhoneNumber.message)}</ErrText>
                        )}
                    </div>

                    {/* Country/Email kept for the backend payload but hidden from the mock-up layout */}
                    <input type="hidden" {...form.register("CountryId", { valueAsNumber: true })} />
                    <input type="hidden" {...form.register("Email")} />
                </div>

                <div className="addr-form-actions">
                    <button type="button" className="addr-form-cancel" onClick={onCancel}>
                        {t("ui.cancel", "Cancel")}
                    </button>
                    <button type="submit" className="addr-form-submit">
                        {t("ui.saveAddress", "Save Address")}
                    </button>
                </div>
            </form>
        </FormProvider>
    );
};

const ErrText: React.FC<React.PropsWithChildren> = ({ children }) => (
    <span
        style={{
            color: "#e74c3c",
            fontFamily: "'Outfit', sans-serif",
            fontSize: 12,
            marginTop: 2,
        }}
    >
        {children}
    </span>
);
